import { logger } from "@/telemetry";

/**
 * Navigate the current window to an absolute URL.
 *
 * Wrapping the assignment in a module function keeps full-page navigation out of the
 * components and hooks that trigger it, so callers such as the Azure AD logout path in
 * `useAppLogout` remain unit-testable. jsdom has no navigation implementation, so a
 * direct `window.location.assign` call cannot be asserted on without replacing the
 * whole `window.location` object.
 *
 * The scheme allowlist is the guard rail on an otherwise generic navigation primitive.
 * Every current caller passes either a tenant-config end-session endpoint or
 * `window.location.origin`, so nothing user-controlled reaches it today, but an exported
 * `redirectTo(url)` with no checks is an open redirect waiting for a future caller. A
 * rejected URL sends the user to the app origin rather than navigating nowhere, so a bad
 * value cannot strand a sign-out on a dead page.
 */
export function redirectTo(url: string): void {
  window.location.assign(safeRedirectUrl(url));
}

/**
 * Reduce a candidate URL to one that is safe to navigate to.
 *
 * Only `https:` is accepted, plus `http:` on localhost so local development against
 * `localhost:5173` still works. Anything else, including `javascript:` and `data:`, falls
 * back to the current origin.
 */
function safeRedirectUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    const isLocalhost = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";

    if (parsed.protocol === "https:" || (parsed.protocol === "http:" && isLocalhost)) {
      return parsed.toString();
    }

    logger.error("Refused to redirect to a URL with an unsupported scheme", undefined, {
      protocol: parsed.protocol,
    });
  } catch {
    // The URL itself is never logged, since an end-session endpoint can carry an id_token_hint.
    logger.error("Refused to redirect to an unparseable URL", undefined, { length: url.length });
  }

  return window.location.origin;
}
